/**
 * Вложения формы «Оценка помещения» (RoomReadinessPage): фото и видео помещения.
 *
 * Фото уходят в JSON заявки (base64, сжатые), видео — НЕ в JSON, а отдельным
 * сообщением в переписку по заявке (`POST /wiki/opening/room-inspection/:id/messages`,
 * multipart, файлы на диск → Object Storage). Партнёр выбрал видео вместо фото
 * (Windows-диалог переключается на «Все файлы») — раньше оно уходило base64 в JSON и
 * заявка падала по лимиту тела. Здесь — чистые правила без DOM и сети, чтобы их
 * можно было проверять тестами.
 */
import { plural } from '../components/ds/tokens';
import type { ThreadLimits } from './api/openingTasks';

/** Не больше фото в одной заявке (как на бэке: `photos.slice(0, 30)`). */
export const MAX_ROOM_PHOTOS = 30;

/**
 * Не больше роликов в форме. В переписку за одно сообщение — до `maxFiles` (10)
 * файлов, поэтому больше 10 уходит несколькими сообщениями; 20 — потолок формы.
 */
export const MAX_ROOM_VIDEOS = 20;

/**
 * Потолок суммарного размера файлов в одном сообщении. nginx wiki.brasol.ru
 * режет тело запроса на `client_max_body_size 1g`: десять роликов по 400 МБ
 * одним сообщением упёрлись бы в 413, поэтому крупные ролики делим на сообщения.
 */
export const MESSAGE_MAX_BYTES = 800 * 1024 * 1024;

/** Текст сообщения-обёртки для видео в переписке. */
export const VIDEO_MESSAGE_BODY = 'Видео помещения';

// ── Разбор выбранных файлов ─────────────────────────────────────────

/** «Не удалось прочитать «x.heic» — сохраните фото как JPG/PNG». */
export function unreadablePhotoMessage(name: string): string {
  return `Не удалось прочитать «${name}» — сохраните фото как JPG/PNG`;
}

/** Имя фото без расширения (для подписи и имени файла на Я.Диске). */
export function photoBaseName(name: string): string {
  return name.replace(/\.[^.]+$/, '');
}

export interface PickedFileLike {
  name: string;
  type: string;
}

export interface PickPlan<T> {
  /** Картинки, которые можно пробовать читать и сжимать (в пределах свободных мест). */
  photos: T[];
  /** Видео из фото-поля — переносим в блок «Видео помещения». */
  videos: T[];
  /** Всё остальное и лишнее — тексты ошибок для партнёра. */
  errors: string[];
}

/**
 * Разбор файлов, выбранных в поле «Фото»: `image/*` — фото, `video/*` — в видео,
 * прочее (PDF, архив, файл без типа) — ошибка, а не «фото» с сырым содержимым.
 * `photoFree` — сколько мест под фото осталось; лишние картинки отбрасываем с ошибкой.
 */
export function planPicked<T extends PickedFileLike>(files: T[], photoFree: number): PickPlan<T> {
  const photos: T[] = [];
  const videos: T[] = [];
  const errors: string[] = [];
  let overflow = 0;
  const free = Math.max(0, photoFree);
  for (const f of files) {
    const mime = (f.type || '').toLowerCase();
    if (mime.startsWith('video/')) {
      videos.push(f);
    } else if (mime.startsWith('image/')) {
      if (photos.length < free) photos.push(f);
      else overflow += 1;
    } else {
      errors.push(unreadablePhotoMessage(f.name));
    }
  }
  if (overflow > 0) {
    errors.push(`Не больше ${MAX_ROOM_PHOTOS} фото — ещё ${overflow} не добавлено.`);
  }
  return { photos, videos, errors };
}

/**
 * Сколько из выбранных роликов ещё влезает в форму (потолок `max` всего).
 * Лишние отбрасываем одной понятной ошибкой.
 */
export function limitVideoSlots<T>(
  picked: T[],
  existing: number,
  max: number = MAX_ROOM_VIDEOS,
): { take: T[]; error: string | null } {
  const free = Math.max(0, max - existing);
  const take = picked.slice(0, free);
  const dropped = picked.length - take.length;
  return {
    take,
    error: dropped > 0 ? `Не больше ${max} видео в заявке — ещё ${dropped} не добавлено.` : null,
  };
}

/** Подсказка под блоком видео: «До 5 минут и до 500 МБ на ролик». */
export function videoLimitHint(limits: Pick<ThreadLimits, 'maxVideoSec' | 'maxVideoMb'>): string {
  const sec = limits.maxVideoSec;
  const m = Math.round(sec / 60);
  const dur = m >= 1 && sec % 60 === 0 ? `${m} ${plural(m, 'минуты', 'минут', 'минут')}` : `${sec} с`;
  return `До ${dur} и до ${limits.maxVideoMb} МБ на ролик`;
}

// ── Отправка видео сообщениями в переписку ──────────────────────────

/**
 * Разбивка роликов по сообщениям: не больше `maxFiles` файлов и не больше
 * `maxBytes` суммарно в одном. Порядок сохраняется; ролик крупнее `maxBytes`
 * (серверный лимит файла его всё равно ограничит) идёт отдельным сообщением.
 */
export function splitIntoMessages<T>(
  items: T[],
  sizeOf: (item: T) => number,
  maxFiles: number,
  maxBytes: number = MESSAGE_MAX_BYTES,
): T[][] {
  const perMessage = Math.max(1, Math.floor(maxFiles));
  const chunks: T[][] = [];
  let cur: T[] = [];
  let curBytes = 0;
  for (const item of items) {
    const size = Math.max(0, sizeOf(item));
    if (cur.length > 0 && (cur.length >= perMessage || curBytes + size > maxBytes)) {
      chunks.push(cur);
      cur = [];
      curBytes = 0;
    }
    cur.push(item);
    curBytes += size;
  }
  if (cur.length > 0) chunks.push(cur);
  return chunks;
}

/** «Видео помещения» / «Видео помещения (2 из 3)» — когда сообщений несколько. */
export function videoMessageBody(index: number, total: number): string {
  return total > 1 ? `${VIDEO_MESSAGE_BODY} (${index + 1} из ${total})` : VIDEO_MESSAGE_BODY;
}

export interface VideosSendResult {
  /** Сколько роликов уже в переписке. */
  sent: number;
  total: number;
  /** Первая ошибка; после неё остальные сообщения не отправляем. */
  error: unknown;
  failed: boolean;
}

/**
 * Отправка роликов по сообщениям по порядку. Не бросает: на первой ошибке
 * останавливается и возвращает, сколько роликов успело уйти (заявка к этому
 * моменту уже создана — сообщать об ошибке надо иначе, чем об отказе заявки).
 */
export async function sendVideosInMessages<T>(
  items: T[],
  opts: { sizeOf: (item: T) => number; maxFiles: number; maxBytes?: number },
  send: (chunk: T[], index: number, total: number) => Promise<unknown>,
): Promise<VideosSendResult> {
  const chunks = splitIntoMessages(items, opts.sizeOf, opts.maxFiles, opts.maxBytes);
  let sent = 0;
  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i]!;
    try {
      await send(chunk, i, chunks.length);
    } catch (error) {
      return { sent, total: items.length, error, failed: true };
    }
    sent += chunk.length;
  }
  return { sent, total: items.length, error: null, failed: false };
}

// ── Тексты ошибок ───────────────────────────────────────────────────

interface ErrorLike {
  status?: unknown;
  body?: unknown;
  message?: unknown;
  name?: unknown;
}

/** Текст `message` из тела ответа сервера (строка или массив ошибок валидации). */
function serverMessageOf(e: unknown): string | null {
  if (!e || typeof e !== 'object') return null;
  const body = (e as ErrorLike).body;
  if (!body || typeof body !== 'object' || !('message' in body)) return null;
  const raw = (body as { message: unknown }).message;
  const text = Array.isArray(raw) ? raw.map(String).join(', ') : typeof raw === 'string' ? raw : null;
  const trimmed = text?.trim();
  return trimmed ? trimmed : null;
}

function statusOf(e: unknown): number | null {
  if (!e || typeof e !== 'object') return null;
  const s = (e as ErrorLike).status;
  return typeof s === 'number' && Number.isFinite(s) ? s : null;
}

/** Обрыв сети / таймаут: `fetch` падает TypeError'ом, а шлюз отвечает 408/502/503/504. */
function isNetworkFailure(e: unknown): boolean {
  if (e instanceof TypeError) return true;
  const name = e && typeof e === 'object' ? (e as ErrorLike).name : null;
  if (name === 'AbortError' || name === 'TimeoutError') return true;
  const status = statusOf(e);
  return status === 408 || status === 502 || status === 503 || status === 504;
}

const NETWORK_TEXT = 'Нет связи с сервером, попробуйте ещё раз';

/**
 * Текст ошибки отправки заявки. Слово сервера (`message` в теле) — как есть;
 * без него — по статусу: 413 (тело не влезло в лимит) → «Слишком большие файлы»,
 * сеть/таймаут → «Нет связи с сервером»; иначе — прежний `fallback`.
 */
export function submitErrorText(e: unknown, fallback: string): string {
  const status = statusOf(e);
  const msg = serverMessageOf(e);
  // Голый «Internal server error» от Nest партнёру ничего не говорит.
  if (msg && !(status != null && status >= 500 && /^internal server error$/i.test(msg))) return msg;
  if (status === 413) return 'Слишком большие файлы — уберите часть фото';
  if (isNetworkFailure(e)) return NETWORK_TEXT;
  return fallback;
}

export interface VideoFailure {
  /** Причина одной фразой (без точки в конце). */
  reason: string;
  /** Переписки у заявки нет (403: заявка без партнёра) — «приложите в переписке» не годится. */
  noThread: boolean;
}

/** Почему видео не легло в переписку: слово сервера, иначе по статусу. */
export function describeVideoFailure(e: unknown): VideoFailure {
  const status = statusOf(e);
  if (status === 403) {
    return { reason: 'переписка по этой заявке вам недоступна', noThread: true };
  }
  const msg = serverMessageOf(e);
  if (msg) return { reason: msg, noThread: false };
  if (status === 413) return { reason: 'видео слишком большое для отправки', noThread: false };
  if (isNetworkFailure(e)) return { reason: 'нет связи с сервером', noThread: false };
  if (status != null && status >= 500) return { reason: `ошибка сервера (${status})`, noThread: false };
  const own = e && typeof e === 'object' ? (e as ErrorLike).message : null;
  if (typeof own === 'string' && own.trim()) return { reason: own.trim(), noThread: false };
  return { reason: 'неизвестная ошибка', noThread: false };
}

/**
 * Что показать партнёру, когда заявка ушла, а видео — нет (или не все).
 * Заявка уже у куратора, поэтому это не «Не удалось отправить заявку».
 */
export function videoFailureNotice(args: { sent: number; total: number; failure: VideoFailure }): string {
  const { sent, total, failure } = args;
  const reason = failure.reason.replace(/[.\s]+$/, '');
  if (sent > 0) {
    const tail = failure.noThread
      ? 'Передайте остальные куратору напрямую.'
      : 'Приложите остальные в переписке по помещению: «Мои задачи» → «Мои помещения».';
    return `Заявка отправлена, но видео загрузилось не полностью (${sent} из ${total}): ${reason}. ${tail}`;
  }
  const many = total > 1;
  const tail = failure.noThread
    ? 'Передайте видео куратору напрямую.'
    : `Приложите ${many ? 'их' : 'его'} в переписке по помещению: «Мои задачи» → «Мои помещения».`;
  return `Заявка отправлена, но видео ${many ? 'не загрузились' : 'не загрузилось'}: ${reason}. ${tail}`;
}
